import { Badge, Button, Card, Icon, Text } from "@shopify/polaris";
import { ArrowRightIcon, KeyIcon } from "@shopify/polaris-icons";
import type { ComponentProps, ReactNode } from "react";
import "./VendorIntegrationCard.css";

type Props = {
    id: string;
    title: string;
    status: "connected" | "disconnected" | "loading" | "error";
    description: string;
    capabilities: { label: string; icon: ComponentProps<typeof Icon>["source"] }[];
    action: { label: string; onAction: () => void };
    footer: string;
    configuration?: { label: string; onAction: () => void };
    details?: ReactNode;
    children?: ReactNode;
};

export default function VendorIntegrationCard({ id, title, status, description, capabilities, action, footer, configuration, details, children }: Props) {
    const connected = status === "connected";
    const labels = { connected: "Connected", disconnected: "Not connected", loading: "Checking connection", error: "Unable to load" };
    return <section className="pf-vendor-integration" aria-labelledby={`${id}-title`} aria-busy={status === "loading"}>
        <Card padding="0">
            <div className="pf-vendor-integration__body">
                <div className="pf-vendor-integration__header">
                    <div className="pf-vendor-integration__identity">
                        <Text as="h2" variant="headingMd" id={`${id}-title`}>{title}</Text>
                        <Badge tone={connected ? "success" : status === "error" ? "critical" : undefined}>{labels[status]}</Badge>
                    </div>
                    <Button variant={status === "disconnected" ? "primary" : "secondary"} onClick={action.onAction} disabled={status === "loading"}>{action.label}</Button>
                </div>
                <Text as="p" tone="subdued">{description}</Text>
                <ul className="pf-vendor-integration__capabilities" aria-label={`${title} capabilities`}>
                    {capabilities.map(({ label, icon }) => <li key={label}><Icon source={icon} tone="subdued" /><span>{label}</span></li>)}
                </ul>
                {details && <details className="pf-vendor-integration__details"><summary>What’s included?</summary><div>{details}</div></details>}
                {children}
            </div>
            <div className="pf-vendor-integration__footer">
                <div className="pf-vendor-integration__context">{status === "disconnected" && <Icon source={KeyIcon} tone="subdued" />}<Text as="p" variant="bodySm" tone="subdued">{footer}</Text></div>
                {connected && configuration && <Button variant="plain" icon={ArrowRightIcon} onClick={configuration.onAction}>{configuration.label}</Button>}
            </div>
        </Card>
    </section>;
}
